import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import {
    createSku,
    CreateSkuPayload,
    loadSingleSkuOptions,
    parseBundleCode,
    SKU_KIND_BUNDLE,
    SkuRow,
    updateSkuGlobal,
} from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { SkuPropSelect } from '../../components/SkuProp';
import { I18n, i18nText } from '../../utils/i18n';
import { toast } from '../../utils/toast';
import { SKU_STATUS_OPTS } from './selectOptions';

interface Props {
    open: boolean;
    /** 编辑目标 (列表行快照, 含组成); null = 新建 */
    sku: SkuRow | null;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onSaved?: () => void;
}

// 集合内一行: 子 SKU + 数量. label = 下拉展示文案 (编码 · 产品名), 选中项可能不在已加载页里, 单独存.
interface ItemDraft {
    key: number;
    skuCode: string;
    label: string;
    qty: string;
    /** 编码解析出来但不能作组成的原因 (不在库 / 是集合); 用户重选后清空 */
    invalid?: string;
}

// 与后端 skuItemMaxQty / skuItemMaxCount 一致.
const MAX_QTY = 9999;
const MAX_ITEMS = 200;
// 输入编码后停顿多久再解析
const PARSE_DEBOUNCE_MS = 400;

let draftSeq = 0;
const newDraft = (skuCode = '', label = '', qty = '1'): ItemDraft => ({
    key: ++draftSeq,
    skuCode,
    label,
    qty,
});

/** 按组成拼 SKU 编码: 沿用后台既有写法, 子编码用 "-" 串起来, 数量 >1 的写成 "编码*N". */
function bundleCodeOf(items: { skuCode: string; qty: number }[]): string {
    return items.map((it) => (it.qty > 1 ? `${it.skuCode}*${it.qty}` : it.skuCode)).join('-');
}

// SKU 集合 新建 / 编辑弹窗 (2026-10-19): 集合 = 若干单品 SKU × 数量;
// 不直接绑定材料、不填规格 (材料与规格看组成 SKU). 提交走审批同单品 SKU.
// 新建时 SKU 编码与组成表格双向联动: 改表格 → 编码按组成重拼; 输入编码 → 后端解析后回填表格.
// 编辑时编码是业务主键不可改, 不联动.
function SkuBundleModal({ open, sku, onClose, onSaved }: Props) {
    const isEdit = !!sku;
    const [skuCode, setSkuCode] = useState('');
    // 最近一次改的是哪一边: 'table' 时编码跟着组成重拼, 'code' 时表格由解析结果回填 (避免两边互相覆盖)
    const editSource = useRef<'code' | 'table'>('table');
    const parseSeq = useRef(0);
    const [parsing, setParsing] = useState(false);
    const [nameZh, setNameZh] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [prop, setProp] = useState('');
    const [status, setStatus] = useState('on_shelf');
    const [cmsId, setCmsId] = useState('');
    const [link, setLink] = useState('');
    const [items, setItems] = useState<ItemDraft[]>([newDraft()]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        editSource.current = 'table';
        setError('');
        setSkuCode(sku?.skuCode || '');
        setNameZh(sku?.productName?.zh || '');
        setNameEn(sku?.productName?.en || '');
        setProp(sku?.prop || '');
        setStatus(sku?.status || 'on_shelf');
        setCmsId(sku && sku.cmsId > 0 ? String(sku.cmsId) : '');
        setLink(sku?.remark || '');
        const its = (sku?.items || []).map((it) =>
            newDraft(
                it.skuCode,
                it.missing
                    ? `${it.skuCode} · (已不在库)`
                    : `${it.skuCode} · ${i18nText(it.productName || {})}`,
                String(it.qty),
            ),
        );
        setItems(its.length > 0 ? its : [newDraft()]);
    }, [open, sku]);

    // 已选的行 (生成编码 / 统计用)
    const picked = useMemo(
        () =>
            items
                .filter((it) => it.skuCode)
                .map((it) => ({ skuCode: it.skuCode, qty: Math.max(1, Number(it.qty) || 1) })),
        [items],
    );
    const autoCode = bundleCodeOf(picked);
    const totalQty = picked.reduce((n, it) => n + it.qty, 0);

    useEffect(() => {
        if (open && !isEdit && editSource.current === 'table') setSkuCode(autoCode);
    }, [open, autoCode, isEdit]);

    // 输入编码 → 防抖解析 → 回填表格 (过期响应按序号丢弃)
    const onCodeInput = (v: string) => {
        editSource.current = 'code';
        setSkuCode(v);
        const seq = ++parseSeq.current;
        if (!v.trim()) {
            setItems([newDraft()]);
            setParsing(false);
            return;
        }
        setParsing(true);
        window.setTimeout(async () => {
            if (seq !== parseSeq.current) return;
            try {
                const res = await parseBundleCode(v);
                if (seq !== parseSeq.current) return;
                const its = (res.items || []).map((it) => ({
                    ...newDraft(
                        it.skuCode,
                        it.invalid
                            ? `${it.skuCode} · (${it.invalid})`
                            : `${it.skuCode} · ${i18nText(it.productName || {})}`,
                        String(it.qty),
                    ),
                    invalid: it.invalid,
                }));
                setItems(its.length > 0 ? its : [newDraft()]);
            } catch (e) {
                if (seq === parseSeq.current) setError(e instanceof ApiError ? e.message : String(e));
            } finally {
                if (seq === parseSeq.current) setParsing(false);
            }
        }, PARSE_DEBOUNCE_MS);
    };

    // 表格侧的改动都经这里: 标记来源, 编码随之重拼
    const editItems = (fn: (list: ItemDraft[]) => ItemDraft[]) => {
        editSource.current = 'table';
        parseSeq.current += 1; // 作废还没回来的解析, 免得它回填覆盖刚改的表格
        setParsing(false);
        setItems(fn);
    };
    const patchItem = (key: number, patch: Partial<ItemDraft>) =>
        editItems((list) => list.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    const removeItem = (key: number) =>
        editItems((list) => (list.length > 1 ? list.filter((it) => it.key !== key) : [newDraft()]));
    const moveItem = (idx: number, delta: number) =>
        editItems((list) => {
            const j = idx + delta;
            if (j < 0 || j >= list.length) return list;
            const next = [...list];
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });

    // 选中已在清单里的 SKU: 合并到原行 (数量 +1), 当前行清空, 避免提交时报重复
    const pickSku = (key: number, value: string, label?: string) => {
        const dup = items.find((it) => it.key !== key && it.skuCode === value && value);
        if (dup) {
            patchItem(dup.key, { qty: String((Number(dup.qty) || 1) + 1) });
            patchItem(key, { skuCode: '', label: '', invalid: undefined });
            toast(`${value} 已在清单中, 数量 +1`);
            return;
        }
        patchItem(key, { skuCode: value, label: label || value, invalid: undefined });
    };

    const onSubmit = async () => {
        const code = skuCode.trim();
        if (!code) {
            setError('SKU 编码必填');
            return;
        }
        if (!nameZh.trim() && !nameEn.trim()) {
            setError('产品中文名称 / 英文名称至少填一个');
            return;
        }
        if (cmsId.trim() && !/^\d+$/.test(cmsId.trim())) {
            setError('CMS 耗材ID 必须是整数');
            return;
        }
        const rows = items.filter((it) => it.skuCode); // 未选 SKU 的空行忽略
        if (rows.length === 0) {
            setError('至少添加 1 个 SKU');
            return;
        }
        const bad = rows.filter((it) => it.invalid);
        if (bad.length > 0) {
            setError(`以下 SKU 不能作为组成, 请重选或移除: ${bad.map((it) => `${it.skuCode} (${it.invalid})`).join('、')}`);
            return;
        }
        for (const it of rows) {
            const q = Number(it.qty);
            if (!Number.isInteger(q) || q < 1 || q > MAX_QTY) {
                setError(`${it.skuCode} 的数量须是 1~${MAX_QTY} 的整数`);
                return;
            }
        }
        if (rows.length > MAX_ITEMS) {
            setError(`SKU 集合最多包含 ${MAX_ITEMS} 个 SKU`);
            return;
        }
        const payloadItems = rows.map((it) => ({ skuCode: it.skuCode, qty: Number(it.qty) }));
        // zh/en 之外的语种原样带回, 不被覆盖丢失
        const productName: I18n = { ...(sku?.productName || {}) };
        delete productName.zh;
        delete productName.en;
        if (nameZh.trim()) productName.zh = nameZh.trim();
        if (nameEn.trim()) productName.en = nameEn.trim();

        setSubmitting(true);
        setError('');
        try {
            let res;
            if (isEdit) {
                res = await updateSkuGlobal(code, {
                    productName,
                    prop,
                    status,
                    cmsId: cmsId.trim() ? Number(cmsId.trim()) : 0,
                    remark: link.trim(),
                    items: payloadItems,
                });
            } else {
                const payload: CreateSkuPayload = {
                    skuCode: code,
                    kind: SKU_KIND_BUNDLE,
                    productName,
                    status,
                    items: payloadItems,
                };
                if (prop) payload.prop = prop;
                if (link.trim()) payload.remark = link.trim();
                if (cmsId.trim()) payload.cmsId = Number(cmsId.trim());
                res = await createSku(payload);
            }
            if (alertApprovalIfPending(res.message)) {
                onClose();
                return;
            }
            toast(isEdit ? `SKU 集合 ${code} 已保存` : `SKU 集合 ${code} 已创建`);
            onSaved?.();
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? `编辑 SKU 集合 · ${sku?.skuCode}` : '新增 SKU 集合'}
            subtitle="由若干单品 SKU × 数量组成 (如材料套装); 不直接绑定材料、不填规格, 材料与规格看组成 SKU"
            width={880}
        >
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="field-label">
                            <span className="text-rose-500">* </span>SKU 编码
                            {isEdit && ' (业务主键不可改; 改组成不会改编码)'}
                        </label>
                        <input
                            className={`inp font-mono w-full ${isEdit ? 'bg-slate-50' : ''}`}
                            value={skuCode}
                            disabled={isEdit}
                            onChange={(e) => onCodeInput(e.target.value)}
                            placeholder="如 P5020890-P5020891*2: 子编码用 - 串联, 数量 >1 写成 *N; 与下方组成双向同步"
                        />
                        {!isEdit && (
                            <div className="text-[11px] text-slate-400 mt-1">
                                {parsing
                                    ? '正在解析编码…'
                                    : '输入编码会解析成下方组成; 改下方组成也会重拼编码'}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>产品中文名称 (中 / 英至少填一个)
                        </label>
                        <input className="inp" value={nameZh} onChange={(e) => setNameZh(e.target.value)} />
                    </div>
                    <div>
                        <label className="field-label">产品英文名称</label>
                        <input className="inp" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                    </div>
                    <div>
                        <label className="field-label">属性</label>
                        <SkuPropSelect className="sel w-full" value={prop} onChange={setProp} />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => setStatus(v)}
                            options={SKU_STATUS_OPTS}
                        />
                    </div>
                    <div>
                        <label className="field-label">CMS 耗材ID (可空)</label>
                        <input
                            className="inp font-mono"
                            value={cmsId}
                            onChange={(e) => setCmsId(e.target.value)}
                            placeholder="仅数字"
                        />
                    </div>
                    <div>
                        <label className="field-label">商品链接 (存入备注)</label>
                        <input
                            className="inp"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            placeholder="https://…"
                        />
                    </div>
                </div>

                <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold">
                            <span className="text-rose-500">* </span>包含的 SKU
                            <span className="ml-2 text-xs font-normal text-slate-500">
                                {picked.length} 种 / 共 {totalQty} 件
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={items.length >= MAX_ITEMS}
                            onClick={() => editItems((list) => [...list, newDraft()])}
                        >
                            + 添加 SKU
                        </button>
                    </div>
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                        <table className="w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="th w-10">#</th>
                                    <th className="th">SKU (只能选单品)</th>
                                    <th className="th w-28">数量</th>
                                    <th className="th w-28">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, idx) => (
                                    <tr key={it.key}>
                                        <td className="td text-xs text-slate-400">{idx + 1}</td>
                                        <td className="td">
                                            <SearchSelect
                                                loadOptions={loadSingleSkuOptions}
                                                value={it.skuCode}
                                                valueLabel={it.label}
                                                onChange={(v, opt) => pickSku(it.key, v, opt?.label)}
                                                placeholder="搜索 SKU 编码 / 产品名"
                                                className="w-full min-w-[18rem]"
                                                clearable
                                            />
                                            {it.invalid && (
                                                <div className="text-[11px] text-rose-500 mt-1">
                                                    {it.skuCode} {it.invalid}, 请重选或移除
                                                </div>
                                            )}
                                        </td>
                                        <td className="td">
                                            <input
                                                type="number"
                                                min={1}
                                                max={MAX_QTY}
                                                step={1}
                                                className="inp w-20 font-mono"
                                                value={it.qty}
                                                onChange={(e) => patchItem(it.key, { qty: e.target.value })}
                                            />
                                        </td>
                                        <td className="td whitespace-nowrap text-xs">
                                            <button
                                                type="button"
                                                className="text-slate-500 hover:text-brand-600 disabled:opacity-30"
                                                title="上移"
                                                disabled={idx === 0}
                                                onClick={() => moveItem(idx, -1)}
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                className="ml-2 text-slate-500 hover:text-brand-600 disabled:opacity-30"
                                                title="下移"
                                                disabled={idx === items.length - 1}
                                                onClick={() => moveItem(idx, 1)}
                                            >
                                                ↓
                                            </button>
                                            <button
                                                type="button"
                                                className="ml-3 text-rose-500 hover:underline"
                                                onClick={() => removeItem(it.key)}
                                            >
                                                移除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="text-[11px] text-slate-400">
                        同一个 SKU 只占一行, 多件请改数量; 集合不能嵌套集合. 行顺序即集合内展示顺序.
                    </div>
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">{error}</div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" className="btn-ghost" disabled={submitting} onClick={onClose}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitting}
                        onClick={onSubmit}
                    >
                        {submitting ? '提交中…' : isEdit ? '保存' : '确定'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default SkuBundleModal;
